<template>
  <ValidationProvider
      :vid="vid"
      :name="name || label"
      :rules="rules"
      :mode="mode"
      v-slot="{ errors, valid, validated }"
      tag="div"
  >
    <div class="omx-field" :class="{ 'has-error': errors[0], 'is-valid': valid && validated && !errors[0], 'is-view-only': viewOnly }">
      <label v-if="label" class="omx-field__label" :for="inputId">
        {{ label }}
        <span v-if="required && !viewOnly" class="omx-field__req" aria-label="required">*</span>
      </label>

      <!-- view-only: static display of the value -->
      <div v-if="viewOnly" class="omx-field__value">
        <slot name="value">{{ displayValue }}</slot>
      </div>

      <!-- text-ish inputs -->
      <div v-else-if="isTextLike" class="omx-field__control" :class="{ 'has-icon': icon }">
        <b-icon v-if="icon" :icon="icon" size="is-small" class="omx-field__icon" />
        <input
            :id="inputId"
            :type="htmlType"
            :value="value"
            :placeholder="placeholder"
            :disabled="disabled"
            :readonly="readonly"
            :autocomplete="autocomplete"
            :tabindex="tabindex"
            @input="$emit('input', $event.target.value)"
            @blur="$emit('blur', $event)"
            @keydown.enter="$emit('enter', $event)"
        />
        <button
            v-if="type === 'password'"
            type="button"
            class="omx-field__reveal"
            @click="reveal = !reveal"
            :aria-label="reveal ? $t('omx-field.hide-password') : $t('omx-field.show-password')"
        >{{ reveal ? $t('omx-field.hide') : $t('omx-field.show') }}</button>
      </div>

      <!-- textarea -->
      <div v-else-if="type === 'textarea'" class="omx-field__control omx-field__control--area">
        <textarea
            :id="inputId"
            :value="value"
            :placeholder="placeholder"
            :disabled="disabled"
            :readonly="readonly"
            :rows="rows"
            @input="$emit('input', $event.target.value)"
            @blur="$emit('blur', $event)"
        />
      </div>

      <!-- number -->
      <div v-else-if="type === 'number'" class="omx-field__control" :class="{ 'has-icon': icon }">
        <b-icon v-if="icon" :icon="icon" size="is-small" class="omx-field__icon" />
        <input
            :id="inputId"
            type="number"
            :value="value"
            :placeholder="placeholder"
            :disabled="disabled"
            :min="min"
            :max="max"
            :step="step"
            @input="$emit('input', toNumber($event.target.value))"
            @blur="$emit('blur', $event)"
        />
      </div>

      <!-- select -->
      <div v-else-if="type === 'select'" class="omx-field__control omx-field__control--select">
        <select
            :id="inputId"
            :value="value"
            :disabled="disabled"
            @change="$emit('input', $event.target.value)"
        >
          <option v-if="placeholder" disabled value="">{{ placeholder }}</option>
          <option v-for="opt in options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
        <b-icon icon="menu-down" size="is-small" class="omx-field__chev" />
      </div>

      <!-- date (delegates to b-datepicker for locale support) -->
      <div v-else-if="type === 'date'" class="omx-field__control omx-field__control--date">
        <b-datepicker
            :id="inputId"
            :value="value"
            :placeholder="placeholder"
            :disabled="disabled"
            :mobile-native="false"
            :locale="dateLocale"
            :max-date="maxDate"
            :min-date="minDate"
            :first-day-of-week="1"
            position="is-bottom-left"
            icon="calendar-blank-outline"
            append-to-body
            show-week-number
            class="omx-datepicker"
            @input="v => $emit('input', v)"
        />
      </div>

      <!-- footer: hint OR first error -->
      <div class="omx-field__foot" v-if="!viewOnly && (hint || errors[0])">
        <p v-if="errors[0]" class="omx-field__error" role="alert">
          <b-icon icon="alert-circle-outline" size="is-small" />
          <span>{{ errors[0] }}</span>
        </p>
        <p v-else-if="hint" class="omx-field__hint">{{ hint }}</p>
      </div>
    </div>
  </ValidationProvider>
</template>

<script>
import { ValidationProvider } from "vee-validate";

/**
 * OmxField — uniform validated form field.
 *
 *   <OmxField v-model="email" type="email" :label="$t('login.email')"
 *             rules="required|email" required icon="email-outline" />
 *
 *   <OmxField v-model="phase" type="select" :label="$t('phase')"
 *             :options="[{value:'plan',label:'Planning'}, ...]" rules="required" />
 *
 *   <OmxField v-model="startDate" type="date" :label="$t('start')" rules="required" />
 *
 * Replaces ad-hoc <b-input>, <b-numberinput>, <b-select> usage when validation
 * matters. Keeps b-datepicker under the hood for locale support.
 */
let UID = 0;

export default {
  name: "OmxField",
  components: { ValidationProvider },
  inheritAttrs: false,
  props: {
    value:       { type: null, default: "" },
    label:       { type: String, default: null },
    type:        { type: String, default: "text" }, // text|email|password|number|textarea|select|date
    placeholder: { type: String, default: null },
    hint:        { type: String, default: null },
    rules:       { type: [String, Object], default: "" },
    name:        { type: String, default: null },
    vid:         { type: String, default: null },
    mode:        { type: String, default: "aggressive" },
    icon:        { type: String, default: null },
    required:    { type: Boolean, default: false },
    disabled:    { type: Boolean, default: false },
    readonly:    { type: Boolean, default: false },
    autocomplete:{ type: String, default: null },
    tabindex:    { type: String, default: null },
    rows:        { type: Number, default: 3 },
    min:         { type: [Number, String], default: null },
    max:         { type: [Number, String], default: null },
    maxDate:     { type: Date, default: null }, // date type only — forwarded to b-datepicker
    minDate:     { type: Date, default: null }, // date type only — forwarded to b-datepicker
    step:        { type: [Number, String], default: null },
    options:     { type: Array, default: () => [] }, // [{ value, label }]
    viewOnly:    { type: Boolean, default: false },
    displayEmpty:{ type: String, default: "—" }
  },
  data() {
    return {
      reveal: false,
      inputId: "omx-f-" + (++UID)
    };
  },
  computed: {
    isTextLike() {
      return ['text', 'email', 'password', 'tel', 'url'].includes(this.type);
    },
    htmlType() {
      if (this.type === 'password' && this.reveal) return 'text';
      return this.type;
    },
    displayValue() {
      const v = this.value;
      if (v == null || v === "") return this.displayEmpty;
      if (this.type === 'select') {
        const opt = this.options.find(o => o.value === v);
        return opt ? opt.label : v;
      }
      if (this.type === 'date' && v instanceof Date) {
        return v.toLocaleDateString(this.$i18n ? this.$i18n.locale : undefined);
      }
      return v;
    },
    dateLocale() {
      const loc = this.$i18n && this.$i18n.locale;
      return loc === 'fr' ? 'fr-FR' : 'en-GB';
    }
  },
  methods: {
    toNumber(v) {
      if (v === "" || v == null) return null;
      const n = Number(v);
      return isNaN(n) ? v : n;
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.omx-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
}

.omx-field__label {
  font-size: 12.5px;
  font-weight: 600;
  color: $omx-ink-700;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.omx-field__req {
  color: $omx-danger-600;
  font-weight: 700;
}

.omx-field__control {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid $omx-ink-200;
  background: #fff;
  border-radius: $omx-radius-md;
  padding: 8px 12px;
  transition: border-color .12s, box-shadow .12s;

  &--area { align-items: stretch; padding: 8px 12px; }
  &--select, &--date { padding: 0; }
  &.has-icon .omx-field__icon { color: $omx-ink-400; flex-shrink: 0; }

  &:focus-within {
    border-color: $omx-brand-500;
    box-shadow: 0 0 0 3px $omx-brand-100;
  }

  input, textarea {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    color: $omx-ink-900;
    font-family: inherit;
    min-width: 0;
    &:disabled { color: $omx-ink-400; cursor: not-allowed; }
  }
  textarea { resize: vertical; min-height: 80px; }
}

.omx-field__control--select {
  position: relative;
  select {
    appearance: none;
    -webkit-appearance: none;
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    padding: 9px 36px 9px 12px;
    font-size: 14px;
    color: $omx-ink-900;
    font-family: inherit;
    cursor: pointer;
    &:disabled { color: $omx-ink-400; }
  }
}
.omx-field__chev {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: $omx-ink-400;
  pointer-events: none;
}

.omx-field__control--date {
  padding: 0;
  ::v-deep .input {
    border: none !important;
    box-shadow: none !important;
    height: auto !important;
    padding: 9px 12px !important;
    font-size: 14px !important;
    color: $omx-ink-900 !important;
    background: transparent !important;
  }
  // Bulma adds .has-icons-left when b-datepicker has an icon; make
  // room for the icon so the date string doesn't collide with it.
  ::v-deep .control.has-icons-left .input {
    padding-left: 36px !important;
  }
  ::v-deep .control.has-icons-left .icon.is-left {
    width: 36px;
    height: 100%;
    color: $omx-ink-400 !important;
  }
  ::v-deep .icon { color: $omx-ink-400 !important; }
}

.omx-field__reveal {
  border: none;
  background: transparent;
  color: $omx-ink-500;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 4px;
  &:hover { color: $omx-brand-600; }
}

.omx-field.has-error .omx-field__control {
  border-color: $omx-danger-600;
  background: #fff;
  &:focus-within { box-shadow: 0 0 0 3px $omx-danger-100; }
}
.omx-field.is-valid .omx-field__control {
  border-color: $omx-success-600;
}

.omx-field__value {
  font-size: 14px;
  color: $omx-ink-900;
  padding: 8px 0 0;
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 22px;
  line-height: 1.4;
}
.omx-field.is-view-only .omx-field__label {
  font-size: 11px;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: $omx-ink-500;
  font-weight: 600;
}
.omx-field.is-view-only {
  margin-bottom: 16px;
  border-bottom: 1px solid $omx-ink-100;
  padding-bottom: 14px;
}
.omx-field.is-view-only:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.omx-field__foot {
  min-height: 16px;
  font-size: 12px;
  line-height: 1.35;
}
.omx-field__error {
  color: $omx-danger-600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-weight: 500;
}
.omx-field__hint {
  color: $omx-ink-500;
  margin: 0;
}
</style>
